import { useMemo, useState } from 'react'
import chevronDoubleRightIcon from '@/assets/icons/chevron-double-right.png'
import { MaterialIcon } from '@/ui/material-icon'
import { Button } from '@/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/ui/dropdown-menu'
import { Skeleton } from '@/ui/skeleton'
import type { RegisteredSourcePickerItem } from '@/features/adp/hooks/useRegisteredDataSources'
import styles from './ChooseDataSourceSheet.module.scss'
import { cn } from '@/shared/lib/utils'

const DATE_RANGE_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
]

const SOURCE_CARD_SKELETON_COUNT = 5

export type ChooseDataSourceSheetProps = {
  onClose: () => void
  /** Pipeline node whose settings opened this panel (for wiring save/back) */
  configuringNodeId?: string | null
  /** When the user picks an existing saved source (data comes from registered list API only). */
  onSelectSource?: (source: RegisteredSourcePickerItem, configuringNodeId: string | null) => void
  /** When the user chooses to add a new source */
  onAddNewSource?: (configuringNodeId: string | null) => void
  isLoading?: boolean
  loadError?: string | null
  sources?: RegisteredSourcePickerItem[]
  selectedDateFilter?: string | null
  onDateFilterChange?: (value: string | null) => void
  searchQuery?: string
  onSearchQueryChange?: (value: string) => void
}

export function ChooseDataSourceSheet({
  onClose,
  configuringNodeId = null,
  onSelectSource,
  onAddNewSource,
  isLoading = false,
  loadError = null,
  sources = [],
  selectedDateFilter = null,
  onDateFilterChange,
  searchQuery = '',
  onSearchQueryChange,
}: Readonly<ChooseDataSourceSheetProps>) {
  const [filtersOpen, setFiltersOpen] = useState(false)

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return sources
    return sources.filter(
      s =>
        s.kind.toLowerCase().includes(q) ||
        s.connectionName.toLowerCase().includes(q)
    )
  }, [searchQuery, sources])

  return (
    <aside
      className={styles.root}
      aria-label="Choose a data source"
      data-configuring-node={configuringNodeId ?? undefined}
    >
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <button
            type="button"
            className={styles.closeChevron}
            onClick={onClose}
            aria-label="Close"
          >
            <img src={chevronDoubleRightIcon} alt="" className={styles.closeChevronIcon} aria-hidden />
          </button>
          <h2 className={styles.title}>Choose a data Source</h2>
        </div>

        <div className={styles.divider} />

        <p className={styles.subtitle}>
          Select an existing source or add a new one to begin configuring and analyzing your data
          quality.
        </p>

        <div className={styles.searchRow}>
          <div className={styles.searchInput}>
            <MaterialIcon name="search" size={18} className={styles.searchGlyph} aria-hidden />
            <input
              type="search"
              placeholder="Search Source"
              value={searchQuery}
              onChange={e => {
                const nextValue = e.target.value
                onSearchQueryChange?.(nextValue)
              }}
              aria-label="Search sources"
            />
          </div>
          <DropdownMenu open={filtersOpen} onOpenChange={setFiltersOpen}>
            <DropdownMenuTrigger asChild>
              <button type="button" className={styles.filtersBtn}>
                <MaterialIcon name="add_circle" size={18} aria-hidden />
                Filters
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-3">
              <DropdownMenuLabel className="mb-2">Date Range</DropdownMenuLabel>
              <DropdownMenuSeparator className="mb-3" />
              <div className={styles.dateRangeContainer}>
                {DATE_RANGE_OPTIONS.map(option => (
                  <Button
                    key={option.value}
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      onDateFilterChange?.(option.value === 'all_time' ? null : option.value)
                      setFiltersOpen(false)
                    }}
                    className={`${styles.dateRangeOptionButton} ${(selectedDateFilter ?? 'all_time') === option.value ? styles.selected : ''}`}
                  >
                    <div className={styles.radioIndicator}>
                      <div className={styles.radioDot} />
                    </div>
                    <span>{option.label}</span>
                  </Button>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className={styles.grid}>
          <button
            type="button"
            className={styles.addCard}
            onClick={() => onAddNewSource?.(configuringNodeId)}
          >
            <MaterialIcon name="add" size={40} className={styles.addIcon} aria-hidden />
            <span className={styles.addLabel}>Add New Source</span>
          </button>

          {isLoading
            ? Array.from({ length: SOURCE_CARD_SKELETON_COUNT }, (_, index) => (
              <div
                key={`source-skeleton-${index + 1}`}
                className={styles.sourceCardSkeleton}
                aria-hidden="true"
              >
                <Skeleton className={styles.sourceCardSkeletonIcon} />
                <div className={styles.sourceCardSkeletonText}>
                  <Skeleton className={styles.sourceCardSkeletonTitle} />
                  <Skeleton className={styles.sourceCardSkeletonSubtitle} />
                  <Skeleton className={styles.sourceCardSkeletonSubtitleShort} />
                </div>
              </div>
            ))
            : filtered.map(s => (
              <button
                key={s.id}
                type="button"
                className={styles.sourceCard}
                onClick={() => onSelectSource?.(s, configuringNodeId)}
              >
                <div className={styles.iconWell}>
                  <img
                    src={s.image}
                    alt={s.databaseName}
                  />
                </div>
                <div className={styles.sourceText}>
                  <span className={styles.sourceKind}>{s.kind}</span>
                  <span className={styles.sourceName}>{s.connectionName}</span>
                </div>
              </button>
            ))}
        </div>
        {!isLoading && loadError && (
          <p className={cn(styles.subtitle, "!mt-2")} role="alert">
            {loadError}
          </p>
        )}
        {!isLoading && !loadError && filtered.length === 0 && (
          <p className={cn(styles.subtitle, "!mt-2")}>No registered sources found.</p>
        )}
      </div>
    </aside>
  )
}
